import React from "react";
import { Edges } from "@react-three/drei";
import { DoubleSide, Texture } from "three";
import { MM, C_PANNELLO } from "./constants";

// Sezione nominale del tubolare di controtelaio sui montanti (100 mm, come nel render 2D).
export const TUBOLARE_SEZIONE = MM(100);

/** Dati che definiscono dove cadono i tubolari rispetto al foro. */
export type TubolariGeometria = {
  xPorta: number;          // bordo sinistro della porta
  zPorta: number;          // centro in Z della porta
  larghezzaPorta: number;
  altezzaPorta: number;
  spessorePorta: number;
  larghezzaForo: number;
  altezzaForo: number;
  inLuceSinistra: boolean; // lati a schermo (vista esterna)
  inLuceDestra: boolean;
  inLuceTraversa: boolean;
};

export type TubolariLayout = {
  sezione: number;
  zTub: number;
  wSx: number;
  wDx: number;
  hVeletta: number;
  xVelSx: number;
  xVelDx: number;
  /**
   * Ingombro complessivo dei tubolari (origine = spigolo basso-sinistro del foro).
   * Il tubolare ha una sezione fissa di 100 mm, mentre lo sguincio disponibile e'
   * quello che avanza fra porta e foro: con le misure standard sono spesso 15-30 mm,
   * quindi il controtelaio sborda oltre il bordo del foro. Il muro usa questo
   * ingombro per allargare lo squarcio, altrimenti i montanti e la veletta
   * finirebbero dentro la muratura e se ne vedrebbe solo una striscia.
   */
  ingombro: { xMin: number; xMax: number; yMax: number };
};

export function getTubolariLayout({
  xPorta,
  zPorta,
  larghezzaPorta,
  altezzaPorta,
  spessorePorta,
  larghezzaForo,
  altezzaForo,
  inLuceSinistra,
  inLuceDestra,
  inLuceTraversa,
}: TubolariGeometria): TubolariLayout {
  const S = TUBOLARE_SEZIONE;

  // I tubolari stanno davanti alla porta (verso l'esterno), dentro lo sguincio.
  const zTub = zPorta + spessorePorta / 2 + S / 2;

  // Montanti: dal bordo porta fino al bordo del foro, mai meno della sezione.
  const wSx = Math.max(xPorta, S);
  const wDx = Math.max(larghezzaForo - (xPorta + larghezzaPorta), S);

  // Veletta: riempie lo spazio fra cima porta e cima foro, a filo dei montanti.
  const hVeletta = Math.max(altezzaForo - altezzaPorta, S);
  // La veletta si allarga oltre il bordo del foro solo sui lati montati in luce:
  // su un lato oltre luce non c'e' nessun montante a cui agganciarsi, quindi il
  // bordo resta a filo foro (altrimenti squarcio e veletta sborderebbero anche li').
  const xVelSx   = inLuceSinistra ? Math.min(xPorta - wSx, 0) : 0;
  const xVelDx   = inLuceDestra ? Math.max(xPorta + larghezzaPorta + wDx, larghezzaForo) : larghezzaForo;

  const xMin = Math.min(
    0,
    inLuceSinistra ? xPorta - wSx : 0,
    inLuceTraversa ? xVelSx : 0,
  );
  const xMax = Math.max(
    larghezzaForo,
    inLuceDestra ? xPorta + larghezzaPorta + wDx : larghezzaForo,
    inLuceTraversa ? xVelDx : larghezzaForo,
  );
  const yMax = Math.max(
    altezzaForo,
    inLuceTraversa ? altezzaPorta + hVeletta : altezzaForo,
  );

  return { sezione: S, zTub, wSx, wDx, hVeletta, xVelSx, xVelDx, ingombro: { xMin, xMax, yMax } };
}

export type TubolariProps = TubolariGeometria & {
  colore: Texture | undefined;
  bordoColore: string;
};

/**
 * Controtelaio dell'installazione in luce: i tubolari sono fissati dentro il foro,
 * a filo esterno della porta, e ci sono solo sui lati installati in luce.
 * I montanti riempiono lo sguincio fino al bordo del foro (almeno la sezione
 * nominale); la veletta e' una fascia a filo del bordo alto del foro.
 */
export const Tubolari: React.FC<TubolariProps> = (props) => {
  const {
    xPorta,
    larghezzaPorta,
    altezzaPorta,
    inLuceSinistra,
    inLuceDestra,
    inLuceTraversa,
    colore,
    bordoColore,
  } = props;

  const { sezione: S, zTub, wSx, wDx, hVeletta, xVelSx, xVelDx } = getTubolariLayout(props);

  const matPorta = {
    map: colore,
    color: colore ? "#ffffff" : C_PANNELLO,
    side: DoubleSide,
  };
  const matKey = colore?.uuid ?? "tubolare-default";

  return (
    <>
      {/* Montante sinistro */}
      {inLuceSinistra && (
        <mesh position={[xPorta - wSx / 2, altezzaPorta / 2, zTub]}>
          <boxGeometry args={[wSx, altezzaPorta, S]} />
          <meshStandardMaterial key={matKey} {...matPorta} />
          <Edges color={bordoColore} />
        </mesh>
      )}

      {/* Montante destro */}
      {inLuceDestra && (
        <mesh position={[xPorta + larghezzaPorta + wDx / 2, altezzaPorta / 2, zTub]}>
          <boxGeometry args={[wDx, altezzaPorta, S]} />
          <meshStandardMaterial key={matKey} {...matPorta} />
          <Edges color={bordoColore} />
        </mesh>
      )}

      {/* Veletta: fascia unica sopra la porta, larga quanto la luce piu' i montanti. */}
      {inLuceTraversa && (
        <mesh position={[(xVelSx + xVelDx) / 2, altezzaPorta + hVeletta / 2, zTub]}>
          <boxGeometry args={[xVelDx - xVelSx, hVeletta, S]} />
          <meshStandardMaterial key={matKey} {...matPorta} />
          <Edges color={bordoColore} />
        </mesh>
      )}
    </>
  );
};
